import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// UUID generation with fallback for environments without crypto.randomUUID
export const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

interface AppState {
  isOnline: boolean
  user: User | null
  token: string | null
  tokenExpires: Date | null
  notifications: Notification[]
  techniciansReportVisible: boolean
  selectedDate: string
  setOnlineStatus: (status: boolean) => void
  setUser: (user: User | null) => void
  setToken: (token: string | null, expires?: Date | null) => void
  logout: () => void
  isAuthenticated: () => boolean
  isTokenExpired: () => boolean
  addNotification: (notification: Omit<Notification, 'id'>) => void
  removeNotification: (id: string) => void
  clearNotifications: () => void
  toggleTechniciansReport: () => void
  setTechniciansReportVisible: (visible: boolean) => void
  setSelectedDate: (date: string) => void
}

interface User {
  id: string
  name: string
  email: string
  username: string
  roles: string[]
  technicianId?: number
}

interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  timestamp: number
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      isOnline: navigator.onLine,
      user: null,
      token: null,
      tokenExpires: null,
      notifications: [],
      techniciansReportVisible: true,
      selectedDate: new Date().toLocaleString('sv').split(' ')[0],
      
      setOnlineStatus: (status: boolean) => {
        set({ isOnline: status })
      },
      
      setUser: (user: User | null) => {
        // Reset to today's date when logging in
        if (user) {
          set({
            user,
            selectedDate: new Date().toLocaleString('sv').split(' ')[0]
          })
        } else {
          set({ user })
        }
      },

      setToken: (token: string | null, expires?: Date | null) => {
        set({ 
          token, 
          tokenExpires: expires || null 
        })
      },

      logout: () => {
        set({ 
          user: null, 
          token: null, 
          tokenExpires: null 
        })
      },

      isAuthenticated: () => {
        const state = get()
        return !!(state.user && state.token && !state.isTokenExpired())
      },

      isTokenExpired: () => {
        const state = get()
        if (!state.tokenExpires) return true
        return new Date() >= new Date(state.tokenExpires)
      },
      
      addNotification: (notification: Omit<Notification, 'id'>) => {
        const newNotification: Notification = {
          ...notification,
          id: generateUUID(),
        }
        set(state => ({
          notifications: [...state.notifications, newNotification]
        }))
      },
      
      removeNotification: (id: string) => {
        set(state => ({
          notifications: state.notifications.filter(n => n.id !== id)
        }))
      },
      
      clearNotifications: () => {
        set({ notifications: [] })
      },

      toggleTechniciansReport: () => {
        set(state => ({ 
          techniciansReportVisible: !state.techniciansReportVisible 
        }))
      },

      setTechniciansReportVisible: (visible: boolean) => {
        set({ techniciansReportVisible: visible })
      },
      
      setSelectedDate: (date: string) => {
        set({ selectedDate: date })
      }
    }),
    {
      name: 'app-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        tokenExpires: state.tokenExpires,
        techniciansReportVisible: state.techniciansReportVisible,
        selectedDate: state.selectedDate
      })
    }
  )
)

// Helper functions for role checking
export const useUserRoles = () => {
  const user = useAppStore(state => state.user)
  
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) ?? false
  }

  const isTechnician = (): boolean => {
    return hasRole('Technician')
  }

  const isAdmin = (): boolean => {
    return hasRole('Admin') || hasRole('Administrator')
  }

  return {
    user,
    hasRole,
    isTechnician,
    isAdmin,
    roles: user?.roles ?? []
  }
}